import React, { useState } from "react";
import { Button, List, Typography, Space, Card, Empty, Spin, Tag } from "antd";
import { useNotificationContext } from "../context/NotificationContext";
import {
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useReadNotifications,
  UserNotification,
} from "../hooks/useNotifications";
import { notificationSound } from "../utils/notificationSound";
import NotificationHeader from "../components/notifications/NotificationHeader";
import NotificationSettings from "../components/notifications/NotificationSettings";

const { Text } = Typography;

const NotificationsPage: React.FC = () => {
  const {
    notifications,
    unreadCount,
    isLoading,
    soundEnabled,
    setSoundEnabled,
    refreshNotifications,
  } = useNotificationContext();

  const [filter, setFilter] = useState<"unread" | "read">("unread");
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [volume, setVolume] = useState(
    () => notificationSound.getVolume() * 100
  );

  // Fetch read notifications when needed
  const {
    data: readNotifications = [],
    isLoading: isLoadingRead,
    refetch: refetchReadNotifications,
  } = useReadNotifications();

  const { mutate: markAsRead } = useMarkNotificationAsRead();
  const { mutate: markAllAsRead, isPending: isMarkingAllAsRead } =
    useMarkAllNotificationsAsRead();

  // Determine which data to use and loading state
  const getFilteredData = () => {
    switch (filter) {
      case "unread":
        return {
          data: notifications.filter((notification) => !notification.read_at),
          isLoading: isLoading,
        };
      case "read":
        return {
          data: readNotifications,
          isLoading: isLoadingRead,
        };
      default: // "all"
        return {
          data: notifications,
          isLoading: isLoading,
        };
    }
  };

  const { data: filteredNotifications, isLoading: currentLoading } =
    getFilteredData();

  // Calculate counts for header
  const readCount = readNotifications.length;

  const handleNotificationClick = (notificationId: string) => {
    markAsRead(notificationId);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleRefresh = () => {
    refreshNotifications();
    if (filter === "read") {
      refetchReadNotifications();
    }
  };

  const handleFilterChange = (newFilter: "unread" | "read") => {
    setFilter(newFilter);
    // Refresh read notifications when switching to read filter
    if (newFilter === "read") {
      refetchReadNotifications();
    }
  };

  const handleVolumeChange = (value: number) => {
    setVolume(value);
    notificationSound.setVolume(value / 100);
  };

  const formatNotificationTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440)
      return `${Math.floor(diffInMinutes / 60)} hours ago`;

    // Show actual date for older notifications
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getNotificationCategory = (message: string): string => {
    if (message.toLowerCase().includes("order")) return "Orders";
    if (message.toLowerCase().includes("table")) return "Tables";
    if (message.toLowerCase().includes("inventory")) return "Inventory";
    if (message.toLowerCase().includes("schedule")) return "Schedule";
    return "General";
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case "Orders":
        return "blue";
      case "Tables":
        return "green";
      case "Inventory":
        return "orange";
      case "Schedule":
        return "purple";
      default:
        return "default";
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      {/* Responsive Header Component */}
      <NotificationHeader
        unreadCount={unreadCount}
        isLoading={currentLoading}
        isMarkingAllAsRead={isMarkingAllAsRead}
        filter={filter}
        readCount={readCount}
        onRefresh={handleRefresh}
        onOpenSettings={() => setSettingsVisible(true)}
        onMarkAllAsRead={handleMarkAllAsRead}
        onFilterChange={handleFilterChange}
      />

      {/* Notifications List */}
      <Card className="shadow-sm">
        {currentLoading ? (
          <div className="text-center py-8">
            <Spin size="large" />
            <div className="mt-4">
              <Text type="secondary">
                Loading{" "}
                {filter === "read"
                  ? "read"
                  : filter === "unread"
                  ? "unread"
                  : ""}{" "}
                notifications...
              </Text>
            </div>
          </div>
        ) : filteredNotifications.length > 0 ? (
          <List
            itemLayout="vertical"
            dataSource={filteredNotifications}
            renderItem={(notification: UserNotification) => {
              const category = getNotificationCategory(
                notification.data.message
              );
              const isUnread = !notification.read_at;

              return (
                <List.Item
                  className={`px-3 sm:px-4 py-3 sm:py-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                    isUnread ? "bg-blue-50 border-l-4 border-l-blue-500" : ""
                  }`}
                  onClick={() => handleNotificationClick(notification.id)}
                  actions={[
                    <Space key="meta" className="flex-wrap">
                      <Tag color={getCategoryColor(category)}>{category}</Tag>
                      <Text type="secondary" className="text-xs">
                        {formatNotificationTime(notification.created_at)}
                      </Text>
                      {isUnread && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                      )}
                    </Space>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Text
                        className={`text-sm sm:text-base ${
                          isUnread
                            ? "font-semibold text-gray-900"
                            : "text-gray-700"
                        }`}
                      >
                        {notification.data.message}
                      </Text>
                    }
                  />
                </List.Item>
              );
            }}
          />
        ) : (
          <div className="text-center py-12">
            <Empty
              description={
                filter === "unread"
                  ? "No unread notifications"
                  : filter === "read"
                  ? "No read notifications"
                  : "No notifications yet"
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={handleRefresh}>
                Refresh Notifications
              </Button>
            </Empty>
          </div>
        )}
      </Card>

      {/* Settings Modal Component */}
      <NotificationSettings
        visible={settingsVisible}
        soundEnabled={soundEnabled}
        volume={volume}
        onClose={() => setSettingsVisible(false)}
        onSoundToggle={setSoundEnabled}
        onVolumeChange={handleVolumeChange}
      />
    </div>
  );
};

export default NotificationsPage;
